import { jsx as _jsx } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2026-07-15 14:50:47 +0200 (Wed, 15 Jul 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 164342 $
//  Purpose   : image viewer layers
// =============================================================================
// ---Third-party imports
import React from 'react';
import * as ReactDOM from 'react-dom';
import now from 'performance-now';
import clsx from 'clsx';
import { Rnd } from 'uix/chunks';
// ---Framework imports
import { Actions } from 'uix/registries';
import { get, flatten, mapItems, isArray, isNil, dict, size, values } from 'ldash';
import log from 'uix/logging';
import { useRef, useMemo, useSelector, useActions, useState, useResettableState, useEffect } from 'uix/hooks';
// ---Local imports
import { useWordData, useFulltextHits, useViewer } from './hooks';
import styles from './layers.css';
// ---Globals
const translate_perc = (x1, y1, x2, y2, w_perc, h_perc)=>{
    /* css style compatible percentage notation
   */ x1 = x1 / w_perc;
    y1 = y1 / h_perc;
    return {
        left: `${x1}%`,
        top: `${y1}%`,
        width: `${x2 / w_perc - x1}%`,
        height: `${y2 / h_perc - y1}%`
    };
};
const RndHighlight = ({ x1, y1, x2, y2, text, label, w_perc, h_perc, className, onHover, onLeaveHover, onClick, onLayerInteract, z_fac, viewerId, page_otid, geomtype, itemId, enableMove })=>{
    /* resizable and draggable geometry element
   */ let setModifiedViewerGeometry = useActions(Actions.setModifiedViewerGeometry);
    // disputable: reject really fast ops, assume mouse accidents
    let interactStartTimestamp = useRef(null);
    let MIN_FAST_CLICK_DUARION = 50 // ms
    ;
    let isFastClick = ()=>now() - interactStartTimestamp.current < MIN_FAST_CLICK_DUARION;
    // position is the top/left corner
    // reset if externally controlled
    let [changedPos, setChangedPos] = useResettableState(null, [
        x1,
        y1
    ]);
    let x = x1;
    let y = y1;
    if (changedPos) {
        ;
        [x, y] = changedPos;
    }
    let w = x2 - x1;
    let h = y2 - y1;
    // reset if externally controlled
    let [changedSize, setChangedSize] = useResettableState(null, [
        w,
        h
    ]);
    if (changedSize) {
        ;
        [w, h] = changedSize;
    }
    let onDragStop = (e, d)=>{
        if (!isFastClick()) {
            setChangedPos([
                Math.round(d.lastX * z_fac),
                Math.round(d.lastY * z_fac)
            ]);
        }
        onLayerInteract(false);
    };
    let onResizeStop = (e, direction, ref, delta)=>{
        if (!isFastClick()) {
            let delta_width = Math.round(delta.width * z_fac);
            let delta_height = Math.round(delta.height * z_fac);
            // probably not needed after react 18
            // but this event is not default batched
            ReactDOM.unstable_batchedUpdates(()=>{
                setChangedSize([
                    w + delta_width,
                    h + delta_height
                ]);
                // update position if left or top resizing
                setChangedPos([
                    direction.endsWith('eft') ? x - delta_width : x,
                    direction.startsWith('top') ? y - delta_height : y
                ]);
            });
        }
        onLayerInteract(false);
    };
    useEffect(()=>{
        if (changedPos || changedSize) {
            let delta = {
                x1: x - x1,
                y1: y - y1,
                x2: x + w - x2,
                y2: y + h - y2
            };
            // prevent delta = (0, 0, 0, 0) updates
            if (values(delta).some((e)=>e)) {
                let prev_bbox = {
                    x1,
                    y1,
                    x2,
                    y2
                };
                let bbox = {
                    x1: x,
                    y1: y,
                    x2: x + w,
                    y2: y + h,
                    delta,
                    prev_bbox
                };
                setModifiedViewerGeometry(viewerId, page_otid, geomtype, {
                    [itemId]: bbox
                }, false);
            }
        }
    }, [
        x,
        y,
        w,
        h
    ]);
    let onDragResizeStart = (e)=>{
        interactStartTimestamp.current = now();
        onLayerInteract(true);
    };
    let size = {
        width: w / z_fac,
        height: h / z_fac
    };
    let enableResizing = size.height > 15 && size.width > 15 || !enableMove;
    return /*#__PURE__*/ _jsx(Rnd, {
        bounds: "parent",
        size: size,
        position: {
            x: x / z_fac,
            y: y / z_fac
        },
        onDragStart: onDragResizeStart,
        onResizeStart: onDragResizeStart,
        onDragStop: onDragStop,
        onResizeStop: onResizeStop,
        enableResizing: enableResizing,
        disableDragging: !enableMove,
        children: /*#__PURE__*/ _jsx("div", {
            style: {
                width: '100%',
                height: '100%',
                cursor: enableMove ? 'move' : undefined
            },
            title: text,
            className: className,
            onMouseEnter: onHover,
            onClick: onClick,
            children: !isNil(label) && /*#__PURE__*/ _jsx("span", {
                className: styles.hl_label,
                children: label
            })
        })
    });
};
const highlight = ({ x1, y1, x2, y2, text, label, w_perc, h_perc, key, as = 'div', className, onHover, onLeaveHover, onClick })=>{
    let bbox_perc = translate_perc(x1, y1, x2, y2, w_perc, h_perc);
    bbox_perc.position = 'absolute';
    className = className || styles.poly;
    if (as == 'div') {
        return /*#__PURE__*/ _jsx("div", {
            style: bbox_perc,
            title: text,
            className: className,
            onMouseEnter: onHover,
            onMouseLeave: onLeaveHover,
            onClick: onClick,
            children: !isNil(label) && /*#__PURE__*/ _jsx("span", {
                className: styles.hl_label,
                children: label
            })
        }, key);
    } else if (as == 'svg') {
        return /*#__PURE__*/ _jsx("svg", {
            style: bbox_perc,
            className: className,
            width: x2 - x1,
            height: y2 - y1,
            onMouseEnter: onHover,
            onMouseLeave: onLeaveHover,
            onClick: onClick,
            children: /*#__PURE__*/ _jsx("title", {
                children: text
            })
        }, key);
    } else {
        log.warn('invalid element name: %o', as);
        return null;
    }
};
const get_perc = (page)=>({
        w_perc: page.size[0] / 100,
        h_perc: page.size[1] / 100
    });
const WordLayer = ({ page })=>{
    if (!page.size) return null // corrupt page
    ;
    if (!page.ocr) return null;
    let { w_perc, h_perc } = get_perc(page);
    //~ if (5890 === page.id) log('render')
    let wordData = useWordData(page.id);
    let words = useMemo(()=>get(wordData, 'words', []).map((word, i)=>{
            // deskewed bounding box in org width/height
            let [x1, y1, x2, y2, text] = word;
            return highlight({
                x1,
                y1,
                x2,
                y2,
                text,
                w_perc,
                h_perc,
                key: i,
                className: styles.word
            });
        }), [
        wordData
    ]);
    if (!words) {
        return null;
    }
    return /*#__PURE__*/ _jsx("div", {
        className: clsx(styles.layer, styles.wordLayer),
        children: words
    });
};
const usePageGeometry = (vid, otid)=>useSelector((root)=>root.data.viewer.geometry[vid]?.[otid]);
const GeometryLayerInteractorDetect = ({ page, z_fac, ...props })=>{
    /* pass through z_fac (triggere re-render on zoom) if there is a interactor geometry
   */ let viewer = useViewer();
    let geometry = usePageGeometry(viewer.vid, page.id);
    let some_resizable = useMemo(()=>mapItems(geometry, (name, items)=>items.some((e)=>e.resizable)).some((e)=>e), [
        geometry
    ]);
    if (!some_resizable && !props.resizable) {
        z_fac = null;
    }
    return /*#__PURE__*/ _jsx(GeometryLayer, {
        page: page,
        z_fac: z_fac,
        ...props
    });
};
const EMPTY_GEOMETRY = {};
const GeometryLayer = ({ page, selectable, resizable, z_fac, onLayerInteract })=>{
    /* used for:
  - ocr-geometry (words, lines, paras, blocks)
  - entity-geometry
  */ let viewer = useViewer();
    let setFocusedViewerGeometry = useActions(Actions.setFocusedViewerGeometry);
    let setFocus = (geomtype, i)=>setFocusedViewerGeometry(viewer.vid, page.id, geomtype, i);
    let setSelectedViewerGeometry = useActions(Actions.setSelectedViewerGeometry);
    // use externally provided geometry for this vid/otid
    let geometry = usePageGeometry(viewer.vid, page.id) || EMPTY_GEOMETRY;
    let focused_geom = useSelector((root)=>root.data.viewer.focusedGeometry[viewer.vid]);
    // internally controlled selection (items need id attribute)
    let selectedGeometry = useSelector((root)=>root.data.viewer.selectedGeometry[viewer.vid]?.[page.id]);
    let handleSelection = (geomtype, item, shiftKey, ctrlKey)=>{
        let prev_selected = selectedGeometry?.[geomtype] || [];
        let next_selected = [
            item.id
        ];
        if (ctrlKey) {
            if (!prev_selected.includes(item.id)) {
                next_selected = [
                    ...prev_selected,
                    item.id
                ];
            } else {
                // XXX: "unselect" does not trigger as interactor rectangle catches mouse events
                next_selected = prev_selected.fiter((e)=>e != item.id);
            }
        } else if (shiftKey && prev_selected.length > 0) {
            let prev_id = prev_selected.at(-1);
            let idx_start = geometry[geomtype].findIndex((i)=>i.id == prev_id);
            let idx_end = geometry[geomtype].findIndex((i)=>i.id == item.id);
            let id_range = geometry[geomtype].slice(idx_start + 1, idx_end + 1).map((i)=>i.id);
            next_selected = [
                ...prev_selected,
                ...id_range
            ];
        }
        setSelectedViewerGeometry(viewer.vid, page.id, geomtype, next_selected, true);
    };
    let focused_items = [];
    if (focused_geom && focused_geom.otid == page.id) {
        let indexes = isArray(focused_geom.index) ? focused_geom.index : [
            focused_geom.index
        ];
        focused_items = indexes.map((index)=>`${focused_geom.geomtype}_${index}`);
    }
    let { w_perc, h_perc } = get_perc(page);
    let boxes = useMemo(()=>mapItems(geometry, (geomtype, items)=>items.map((item, i)=>{
                // deskewed bounding box in org width/height
                let is_hidden = item.hidden && !item.selected;
                let { x1, y1, x2, y2 } = item.bbox;
                let is_focused = focused_items.includes(`${geomtype}_${i}`);
                // use item className or generate from other item props
                let _className = item.className || clsx(styles.poly, is_focused && styles.focused, is_hidden && styles.hidden, item.selected && styles.selected, selectable && (selectedGeometry?.[geomtype] || []).includes(item.id) && styles.selected, selectable && styles.selectable, item.deleted && styles.deleted, item.modified && styles.modified, item.highlight && styles.highlight, styles[`geo_${geomtype}`]);
                let hl_props = {
                    x1,
                    y1,
                    x2,
                    y2,
                    text: item.text,
                    label: is_hidden ? null : item.label,
                    w_perc,
                    h_perc,
                    key: `${geomtype}_${i}`,
                    className: _className,
                    onHover: item.hoverFocus ? ()=>setFocus(geomtype, i) : null,
                    onLeaveHover: item.hoverFocus ? ()=>setFocus(geomtype, null) : null,
                    // need a item.id for single click selection
                    onClick: selectable && !isNil(item.id) && geomtype != 'interactor' ? (e)=>handleSelection(geomtype, item, e.shiftKey, e.ctrlKey) : null
                };
                if (!is_hidden && (resizable || item.resizable === true || // XXX: this selected is only here because external resizable seems to be buggy after move-op
                item.resizable === 'focused' && (is_focused || item.selected))) {
                    hl_props = {
                        ...hl_props,
                        onLayerInteract,
                        z_fac,
                        viewerId: viewer.vid,
                        page_otid: page.id,
                        geomtype,
                        itemId: item.id,
                        enableMove: !!item.resizable_move
                    };
                    return /*#__PURE__*/ _jsx(RndHighlight, {
                        ...hl_props
                    });
                }
                return highlight(hl_props);
            })), [
        geometry,
        focused_items,
        selectable,
        selectedGeometry,
        z_fac
    ]);
    if (selectable) {
        let style = translate_perc(0, 0, page.size[0], page.size[1], w_perc, h_perc);
        let deselector = /*#__PURE__*/ _jsx(Deselector, {
            style: {
                ...style,
                position: 'absolute'
            },
            onClick: ()=>{
                setSelectedViewerGeometry(viewer.vid, page.id, null, null, true);
            }
        }, "deselect");
        // needs to come first for z-ordering
        boxes = [
            deselector,
            ...boxes
        ];
    }
    return /*#__PURE__*/ _jsx("div", {
        className: clsx(styles.layer, styles.geometryLayer),
        children: boxes
    });
};
const Deselector = ({ onClick, ...props })=>{
    /* only run onClick if mouse has not moved (could be a rect-select op)
   */ let pos = useRef(null);
    // Handle the mousedown: record the position.
    let handleMouseDown = (e)=>{
        pos.current = {
            x: e.clientX,
            y: e.clientY
        };
    };
    // Handle the mouseup: compare the current position with the stored one.
    let handleMouseUp = (e)=>{
        if (pos.current && e.clientX === pos.current.x && e.clientY === pos.current.y) {
            onClick(e);
        }
        pos.current = null; // reset the ref after a mouseup event
    };
    return /*#__PURE__*/ _jsx("div", {
        ...props,
        onMouseDown: handleMouseDown,
        onMouseUp: handleMouseUp
    });
};
const SearchLayerTerms = ({ vid, page, thumbs })=>{
    let hits = useFulltextHits({
        vid,
        otid: page.id
    });
    if (thumbs) {
        hits = {};
    }
    let [orgWidth, orgHeight] = page.size;
    let w_perc = orgWidth / 100;
    let h_perc = orgHeight / 100;
    let markterms = useMemo(()=>flatten(mapItems(hits || {}, (term, hits)=>hits.map((bbox, i)=>{
                // obscure (top, left), (bottom, right) order
                let [x1, y1] = bbox[0];
                let [x2, y2] = bbox[1];
                //~ let enlargeByY = (y2 - y1) * 0.15 // by 15% line height
                //~ y1 = y1 - enlargeByY
                //~ y2 = y2 + enlargeByY
                return highlight({
                    x1,
                    y1,
                    x2,
                    y2,
                    term,
                    w_perc,
                    h_perc,
                    key: `${term}_${i}`,
                    className: styles.searchterm
                });
            }))), [
        hits
    ]);
    return /*#__PURE__*/ _jsx("div", {
        className: clsx(styles.layer, styles.searchLayer, thumbs && styles.thumbs),
        children: markterms
    });
};
const SearchLayer = ({ page, thumbs })=>{
    let viewer = useViewer();
    let searchData = useSelector((root)=>root.data.viewer.activeInBookSearches[viewer.vid]);
    let isPageHit = searchData ? searchData.pageById[page.id] : false;
    if (!isPageHit) {
        return null;
    } else {
        return /*#__PURE__*/ _jsx(SearchLayerTerms, {
            vid: viewer.vid,
            page: page,
            thumbs: thumbs
        });
    }
};
export const Layers = {
    words: {
        //~ init: () => {},
        enabled: ({ area, zoom, opts })=>!opts?.minZoom || zoom > opts.minZoom,
        render: ({ area, zoom })=>/*#__PURE__*/ _jsx(WordLayer, {
                page: area.page
            }, "words")
    },
    geometry: {
        //~ init: () => {},
        enabled: ({ area, zoom, opts })=>!opts?.minZoom || zoom > opts.minZoom,
        render: ({ area, zoom, opts, onLayerInteract })=>// depends on area (instead of area.page, z_fac pass-through) info for resizable support
            // if react-rnd would support percentages in position (instead only in size)
            // we could use the simpler/faster area.page (no mutation on zooming)
            // see https://github.com/bokuweb/react-rnd/issues/611
            /*#__PURE__*/ _jsx(GeometryLayerInteractorDetect, {
                z_fac: area.orgWidth / area.width,
                page: area.page,
                selectable: opts.selectable,
                resizable: opts.resizable,
                onLayerInteract: onLayerInteract
            }, "polygons")
    },
    search: {
        //~ init: () => {},
        render: ({ area, zoom, opts })=>/*#__PURE__*/ _jsx(SearchLayer, {
                thumbs: zoom <= opts.thumbZoom,
                page: area.page
            }, 'search')
    }
};
